import { Paper } from "@mui/material";
import clsx from "clsx";
import { useProviderSearchContext } from "context/ProviderSearchContext";
import { LINKS_RECARE } from "core/consts";
import { ProviderSearchConfig } from "core/model/providerSearch/config";
import { isPrintMode } from "core/model/utils/urls";
import { getYear } from "date-fns";
import { Link } from "ds/ui";
import { LogoWrapper } from "ds_legacy/components/AppBar";
import { useExternalLinkLabel } from "ds_legacy/components/LinkV2";
import { useMessengerTabs } from "ds_legacy/components/Tabs/Messenger";
import { padding, sizing } from "ds_legacy/materials/metrics";
import { SHADOW_DP8 } from "ds_legacy/materials/shadows";
import Image from "dsl/atoms/Image";
import { useLegalDocuments } from "dsl/atoms/LegalDocuments";
import { useMedia } from "dsl/atoms/ResponsiveMedia";
import { useTranslations } from "translations";

const DevelopedBy = ({ className }: { className?: string }) => {
  const translations = useTranslations();
  const externalLinkLabel = useExternalLinkLabel();

  return (
    <div className={clsx("flex flex-col overflow-visible", className)}>
      <p className="mb-2">{translations.providersearch.footer.developedBy}</p>
      <Link
        className="w-fit"
        href={LINKS_RECARE.home}
        id="recare-logo-footer"
        isExternal
        target="_blank"
      >
        <Image
          alt={externalLinkLabel(
            translations.providersearch.footer.altTextRecareLogo,
          )}
          className="h-[48px] self-end desktop:h-[64px]"
          path="logo/svg/recare_logo_rgb_master.svg"
        />
      </Link>
    </div>
  );
};

const Sponsors = () => {
  const translations = useTranslations();
  const externalLinkLabel = useExternalLinkLabel();
  const {
    config: {
      footer: { sponsors },
    },
  } = useProviderSearchContext();

  const renderSponsor = () => (
    <Image
      alt={externalLinkLabel(sponsors.altText(translations))}
      className={clsx("pt-1", sponsors.className)}
      path={sponsors.logo}
    />
  );

  return (
    <div className="mb-4 flex flex-wrap justify-between gap-4 overflow-visible">
      {sponsors.url ? (
        <Link
          href={sponsors.url}
          id="sponsor-logo-footer"
          isExternal
          target="_blank"
        >
          {renderSponsor()}
        </Link>
      ) : (
        renderSponsor()
      )}
      <DevelopedBy className="hidden desktop:block" />
    </div>
  );
};

const ParticipantsAndImplementation = () => {
  const translations = useTranslations();
  const externalLinkLabel = useExternalLinkLabel();
  const {
    config: {
      footer: { participants },
    },
  } = useProviderSearchContext();

  return (
    <div className="flex flex-wrap justify-between gap-1">
      {participants.items.length > 0 && (
        <div className="my-3 flex flex-col overflow-visible">
          <p className="mb-1 font-bold">
            {translations.providersearch.footer.inCollaborationWith}
          </p>
          <div className="flex flex-col flex-wrap gap-x-5 gap-y-1 overflow-visible tablet:flex-row">
            {participants.items.map((participant) => {
              const label = participant.label(translations);
              if (participant.href) {
                return (
                  <Link
                    key={participant.id}
                    aria-label={externalLinkLabel(label)}
                    className="body-md w-fit text-black"
                    href={participant.href}
                    id={`participant-${participant.id}`}
                    isExternal
                    target="_blank"
                    trackEventProps={{
                      id: `participant`,
                      participant_id: participant.id,
                    }}
                  >
                    {label}
                  </Link>
                );
              }
              return (
                <p key={participant.id} className="body-md mb-0.5">
                  {label}
                </p>
              );
            })}
          </div>
        </div>
      )}
      <hr className="border-gray-900/12 h-[1px] w-full desktop:hidden" />
      <DevelopedBy className="desktop:hidden" />
    </div>
  );
};

const FooterListItem = ({
  item,
}: {
  item: ProviderSearchConfig["footer"]["links_and_contact_info"]["items"][number];
}) => {
  const translations = useTranslations();
  const externalLinkLabel = useExternalLinkLabel();
  const { getDocumentUrl } = useLegalDocuments();
  const href = item.legalDocumentType
    ? getDocumentUrl(item.legalDocumentType)
    : item.href?.(translations);
  const Icon = item.icon;

  if (href) {
    return (
      <Link
        aria-label={
          item.ariaLabel
            ? externalLinkLabel(
                item.ariaLabel.label(translations),
                item.ariaLabel.type,
              )
            : undefined
        }
        className="m-0 flex items-center justify-center gap-2 text-black"
        href={href}
        id={`footer-link-${item.id}`}
        isExternal
        target="_blank"
        trackEventProps={{
          id: `footer`,
        }}
        type={item.type}
      >
        {Icon ? <Icon className="h-4 w-4 text-black" /> : null}
        <span className="text-body-md font-bold text-black">
          {item.label(translations)}
        </span>
      </Link>
    );
  }

  return <p className="mb-2 text-body-md">{item.label(translations)}</p>;
};

const LinksAndContactInfo = () => {
  const {
    config: {
      footer: { links_and_contact_info },
    },
  } = useProviderSearchContext();

  return (
    <ul className="mt-6 flex list-none flex-wrap overflow-visible">
      {links_and_contact_info.items.map((item) => {
        return (
          <li key={item.id} className="mb-2 mr-8">
            <FooterListItem item={item} />
          </li>
        );
      })}
    </ul>
  );
};

const Copyright = () => {
  const translations = useTranslations();
  return (
    <p className="text-body-md">
      {translations.providersearch.footer.copyright({
        year: getYear(new Date()).toString(),
      })}
    </p>
  );
};

const AccessibilityCertificate = () => {
  const translations = useTranslations();
  const externalLinkLabel = useExternalLinkLabel();
  const {
    config: {
      footer: { accessibilityCertificate },
    },
  } = useProviderSearchContext();

  if (!accessibilityCertificate?.logo || !accessibilityCertificate?.url)
    return null;

  return (
    <LogoWrapper href={accessibilityCertificate.url} target="_blank">
      <Image
        alt={externalLinkLabel(
          translations.providersearch.footer.altTextAccessibilityCertificate,
        )}
        src={accessibilityCertificate.logo}
        style={{ height: sizing(5) }}
      />
    </LogoWrapper>
  );
};

const CopyrightAndAccessibilityCertificate = () => {
  return (
    <div className="mt-6 flex flex-wrap-reverse items-start justify-between gap-8 overflow-visible tablet:mt-0">
      <Copyright />
      <AccessibilityCertificate />
    </div>
  );
};

export const Footer = () => {
  const { isDesktop } = useMedia();
  const isPrint = isPrintMode();
  const { activeTab } = useMessengerTabs();

  if (activeTab === "messenger" || isPrint) return null;

  return (
    <Paper
      component="footer"
      sx={{
        "@media print": {
          display: "none",
          padding: "0",
        },
        boxShadow: SHADOW_DP8,
        overflow: "visible",
        padding: isDesktop ? padding(5, 5, 2, 5) : padding(3),
      }}
    >
      <div className="flex flex-col overflow-visible">
        <Sponsors />
        <ParticipantsAndImplementation />
        <hr className="divider w-full desktop:hidden" />
        <LinksAndContactInfo />
        <CopyrightAndAccessibilityCertificate />
      </div>
    </Paper>
  );
};
